import React, { useMemo } from "react";
import {
  CategoryOutlined,
  LocalMallOutlined,
  InsightsOutlined,
  StorefrontOutlined,
  AutorenewOutlined,
  FlagOutlined,
  TaskAltOutlined,
  PendingActionsOutlined,
  AttachMoneyOutlined,
} from "@mui/icons-material";

export function ClientesMetaResumen({
  userData,
  clientesMetaData,
  clientesFacturadosMetaData,
  montoBonoClientesBuenosData,
}) {
  const esGrupoPermitido = Number(userData?.idGrupo) === 24 || Number(userData?.idGrupo) === 23 || Number(userData?.idGrupo) === 22 || Number(userData?.idGrupo) === 27;

  const metas = useMemo(
    () => (Array.isArray(clientesMetaData) ? clientesMetaData : []),
    [clientesMetaData]
  );
  const facturados = useMemo(
    () => (Array.isArray(clientesFacturadosMetaData) ? clientesFacturadosMetaData : []),
    [clientesFacturadosMetaData]
  );

  const resumenTipos = useMemo(() => {
    const normalizar = (texto) =>
      String(texto || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toUpperCase();

    const prioridadTipos = {
      "BANCARIZADO A": 1,
      "RECURRENTE": 2,
    };

    const metasPorTipo = metas.reduce((acumulador, item) => {
      const tipo = item?.Tipo || "N/A";
      acumulador[tipo] = (acumulador[tipo] || 0) + (Number(item?.Meta) || 0);
      return acumulador;
    }, {});

    const facturadosPorTipo = facturados.reduce((acumulador, item) => {
      const tipo = item?.Tipo || "N/A";
      acumulador[tipo] = (acumulador[tipo] || 0) + (Number(item?.Meta) || 0);
      return acumulador;
    }, {});

    const tiposUnicos = [...new Set([
      ...Object.keys(metasPorTipo),
      ...Object.keys(facturadosPorTipo),
    ])];

    return tiposUnicos
      .map((tipo) => {
        const totalMetaTipo = metasPorTipo[tipo] || 0;
        const totalFacturadoTipo = facturadosPorTipo[tipo] || 0;
        const cumplimiento = totalMetaTipo > 0
          ? Math.min((totalFacturadoTipo / totalMetaTipo) * 100, 100)
          : 0;
        const pendiente = Math.max(totalMetaTipo - totalFacturadoTipo, 0);

        return {
          tipo,
          totalMetaTipo,
          totalFacturadoTipo,
          cumplimiento,
          pendiente,
        };
      })
      .sort((a, b) => {
        const prioridadA = prioridadTipos[normalizar(a.tipo)] || 999;
        const prioridadB = prioridadTipos[normalizar(b.tipo)] || 999;

        if (prioridadA !== prioridadB) {
          return prioridadA - prioridadB;
        }

        return b.cumplimiento - a.cumplimiento;
      });
  }, [metas, facturados]);

  const formatearNumero = (valor) => Number(valor || 0).toLocaleString("es-EC");
  const esGrupo23 = Number(userData?.idGrupo) === 23;

  const bonosPorTipoCliente = useMemo(() => {
    const dataBonos = Array.isArray(montoBonoClientesBuenosData) ? montoBonoClientesBuenosData : [];
    const obtenerIdTipo = (item) =>
      Number(
        item?.idTipoCliente ??
        item?.IdTipoCliente ??
        item?.idTipo ??
        item?.TipoCliente ??
        0
      );

    const metaPorIdTipo = metas.reduce((acumulador, item) => {
      const idTipo = obtenerIdTipo(item);
      if (idTipo > 0) {
        acumulador[idTipo] = (acumulador[idTipo] || 0) + (Number(item?.Meta) || 0);
      }
      return acumulador;
    }, {});

    const facturadoPorIdTipo = facturados.reduce((acumulador, item) => {
      const idTipo = obtenerIdTipo(item);
      if (idTipo > 0) {
        acumulador[idTipo] = (acumulador[idTipo] || 0) + (Number(item?.Meta) || 0);
      }
      return acumulador;
    }, {});

    const normalizar = (texto) =>
      String(texto || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toUpperCase();

    const obtenerValor = (idTipoCliente) => {
      const item = dataBonos.find((registro) => Number(registro?.idTipoCliente) === idTipoCliente);
      return Number(item?.Valor) || 0;
    };

    const obtenerResumenTipo = (tipoObjetivo) => {
      const tipoNormalizado = normalizar(tipoObjetivo);

      return (
        resumenTipos.find((item) => normalizar(item?.tipo) === tipoNormalizado) ||
        resumenTipos.find((item) => normalizar(item?.tipo).includes(tipoNormalizado))
      );
    };

    const obtenerMetaFactPorTipo = (idTipoCliente, tipoObjetivo) => {
      const metaDirecta = Number(metaPorIdTipo[idTipoCliente]) || 0;
      const facturadoDirecto = Number(facturadoPorIdTipo[idTipoCliente]) || 0;

      if (metaDirecta > 0 || facturadoDirecto > 0) {
        return { meta: metaDirecta, facturado: facturadoDirecto };
      }

      const resumen = obtenerResumenTipo(tipoObjetivo);
      return {
        meta: Number(resumen?.totalMetaTipo) || 0,
        facturado: Number(resumen?.totalFacturadoTipo) || 0,
      };
    };

    const calcularBono = (idTipoCliente, tipoObjetivo, valorUnitario) => {
      const { meta, facturado } = obtenerMetaFactPorTipo(idTipoCliente, tipoObjetivo);
      const excedente = Math.max(facturado - meta, 0);
      const superaMeta = facturado > meta;
      const baseMultiplicacion = esGrupo23 ? excedente : facturado;
      const bonoCalculado = superaMeta ? baseMultiplicacion * valorUnitario : 0;

      return {
        meta,
        facturado,
        excedente,
        superaMeta,
        baseMultiplicacion,
        bonoCalculado,
      };
    };

    const valorTipo2 = obtenerValor(2);
    const valorTipo9 = obtenerValor(9);
    const calculoTipo2 = calcularBono(2, "BANCARIZADO A", valorTipo2);
    const calculoTipo9 = calcularBono(9, "RECURRENTE", valorTipo9);

    return [
      {
        idTipoCliente: 2,
        titulo: "BANCARIZADO A",
        valorUnitario: valorTipo2,
        ...calculoTipo2,
      },
      {
        idTipoCliente: 9,
        titulo: "RECURRENTE",
        valorUnitario: valorTipo9,
        ...calculoTipo9,
      },
    ];
  }, [montoBonoClientesBuenosData, resumenTipos, esGrupo23]);

  const obtenerEstado = (cumplimiento) => {
    if (cumplimiento >= 100) {
      return {
        etiqueta: "Cumplido",
        badge: "bg-emerald-100 text-emerald-800 border border-emerald-200",
        bar: "bg-emerald-600",
      };
    }

    if (cumplimiento >= 70) {
      return {
        etiqueta: "En progreso",
        badge: "bg-amber-100 text-amber-800 border border-amber-200",
        bar: "bg-amber-500",
      };
    }

    return {
      etiqueta: "Por gestionar",
      badge: "bg-rose-100 text-rose-800 border border-rose-200",
      bar: "bg-rose-500",
    };
  };

  if (!esGrupoPermitido) {
    return null;
  }

  return (
    <section className="mb-3">
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
            <CategoryOutlined sx={{ fontSize: 18 }} className="text-indigo-700" />
            Dashboard Retail · Criterios por tipo
          </p>
          <span className="text-[11px] px-2.5 py-0.5 rounded-full bg-white border border-slate-200 text-indigo-700 font-semibold">
            Dashboard
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          {bonosPorTipoCliente.map((bono) => (
            <div
              key={bono.idTipoCliente}
              className={`rounded-xl p-3.5 shadow-sm flex items-center justify-between border ${
                bono.idTipoCliente === 2
                  ? "border-emerald-200 bg-emerald-50"
                  : "border-blue-200 bg-blue-50"
              }`}
            >
              <div>
                <p className={`text-sm font-semibold ${bono.idTipoCliente === 2 ? "text-emerald-700" : "text-blue-700"}`}>
                  BONO GANADO {bono.titulo}
                </p>
                <p className={`text-2xl font-bold leading-none mt-1 ${bono.idTipoCliente === 2 ? "text-emerald-800" : "text-blue-800"}`}>
                  ${formatearNumero(bono.bonoCalculado)}
                </p>
                <p className="text-[11px] text-slate-600 mt-1">
                  Meta: {formatearNumero(bono.meta)} · Fact.: {formatearNumero(bono.facturado)}
                </p>
                <p className="text-[11px] font-semibold text-slate-700 mt-0.5">
                  {bono.superaMeta
                    ? esGrupo23
                      ? `Excedente ${formatearNumero(bono.excedente)} × $${formatearNumero(bono.valorUnitario)}`
                      : `Facturados ${formatearNumero(bono.facturado)} × $${formatearNumero(bono.valorUnitario)}`
                    : "Meta no superada · Bono $0"}
                </p>
              </div>
              <div className={`w-11 h-11 rounded-lg flex items-center justify-center border ${bono.idTipoCliente === 2 ? "bg-emerald-100 border-emerald-200" : "bg-blue-100 border-blue-200"}`}>
                {bono.idTipoCliente === 2 ? (
                  <StorefrontOutlined className="text-emerald-700" sx={{ fontSize: 22 }} />
                ) : (
                  <AutorenewOutlined className="text-blue-700" sx={{ fontSize: 22 }} />
                )}
              </div>
            </div>
          ))}
        </div>

        {resumenTipos.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-500">
            No hay datos de metas/facturados para mostrar en el dashboard.
          </div>
        ) : (
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))" }}
          >
            {resumenTipos.map((item) => {
              const estado = obtenerEstado(item.cumplimiento);
              const anguloTipo = (item.cumplimiento / 100) * 360;

              return (
                <div
                  key={item.tipo}
                  className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm h-full flex flex-col justify-between"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-600 flex items-center gap-1">
                        <LocalMallOutlined sx={{ fontSize: 16 }} className="text-indigo-600" />
                        Tipo cliente
                      </p>
                      <p className="text-base text-slate-800 truncate mt-0.5 font-bold">{item.tipo}</p>
                    </div>
                    <span className={`text-[11px] px-2.5 py-0.5 rounded-full font-semibold ${estado.badge}`}>
                      {estado.etiqueta}
                    </span>
                  </div>

                  <div className="mt-2.5 flex items-center gap-2.5">
                    <div
                      className="relative w-[78px] h-[78px] rounded-full shrink-0"
                      style={{
                        background: `conic-gradient(#4338ca ${anguloTipo}deg, #c7d2fe ${anguloTipo}deg 360deg)`,
                      }}
                    >
                      <div className="absolute inset-[10px] rounded-full bg-white flex items-center justify-center">
                        <div className="flex flex-col items-center leading-none">
                          <InsightsOutlined sx={{ fontSize: 14 }} className="text-indigo-700 mb-0.5" />
                          <span className="text-base font-bold text-indigo-800">{item.cumplimiento.toFixed(0)}%</span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 w-full">
                      <div className="h-[76px] rounded-lg bg-slate-100 border border-indigo-200 px-2 py-1.5 flex flex-col justify-between">
                        <p className="text-[11px] text-indigo-800 font-semibold flex items-center gap-1">
                          <FlagOutlined sx={{ fontSize: 16 }} />
                          Meta
                        </p>
                        <p className="text-base font-bold text-indigo-800 leading-none">{formatearNumero(item.totalMetaTipo)}</p>
                      </div>
                      <div className="h-[76px] rounded-lg bg-slate-100 border border-emerald-200 px-2 py-1.5 flex flex-col justify-between">
                        <p className="text-[11px] text-emerald-800 font-semibold flex items-center gap-1">
                          <TaskAltOutlined sx={{ fontSize: 16 }} />
                          Fact.
                        </p>
                        <p className="text-base font-bold text-emerald-800 leading-none">{formatearNumero(item.totalFacturadoTipo)}</p>
                      </div>
                      <div className="h-[76px] rounded-lg bg-slate-100 border border-slate-300 px-2 py-1.5 flex flex-col justify-between">
                        <p className="text-[11px] text-slate-700 font-semibold flex items-center gap-1">
                          <PendingActionsOutlined sx={{ fontSize: 16 }} />
                          Pend.
                        </p>
                        <p className="text-base font-bold text-slate-800 leading-none">{formatearNumero(item.pendiente)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 h-2 rounded-full bg-slate-200 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${estado.bar}`}
                      style={{ width: `${item.cumplimiento}%` }}
                    />
                  </div>
                  <p className="mt-1 text-xs text-slate-700 font-semibold">
                    {item.cumplimiento.toFixed(1)}% cumplido
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
